import React, { useEffect, useRef, useState } from "react";
import type { ColumnMeta } from "../../type";
import useFilterStore from "../../store/useFilterStore";

interface Props {
  value: unknown;
  column: ColumnMeta;
  isDirty: boolean;
  row?: any;
  onCommit: (newValue: unknown) => void;
}

const formatDisplay = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const parseByType = (raw: string, type: ColumnMeta["type"]): unknown => {
  if (raw === "") return null;
  if (type === "integer") {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "number") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "boolean") {
    return raw === "true" || raw === "1";
  }
  return raw;
};

const isNumericType = (t: ColumnMeta["type"]) =>
  t === "integer" || t === "number";

const EditableCell: React.FC<Props> = ({
  value,
  column,
  isDirty,
  row,
  onCommit,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  const { filterData, fetchFilterData } = useFilterStore();

  useEffect(() => {
    if (editing) {
      if (column.name === "experiment_id" && !filterData) {
        fetchFilterData();
      } else if (column.name !== "experiment_id") {
        inputRef.current?.focus();
        inputRef.current?.select();
      } else {
        // focus select element
        inputRef.current?.focus();
      }
    }
  }, [editing, column.name, filterData, fetchFilterData]);

  if (!column.editable) {
    return (
      <td
        className="px-3 py-2 text-xs text-gray-400 bg-gray-50 cursor-not-allowed whitespace-nowrap border-r border-gray-100"
        title="Read-only column"
      >
        {formatDisplay(value)}
      </td>
    );
  }

  const startEdit = () => {
    setDraft(value === null || value === undefined ? "" : String(value));
    setEditing(true);
  };

  const commit = () => {
    const parsed = parseByType(draft, column.type);
    onCommit(parsed);
    setEditing(false);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    if (column.name === "experiment_id") {
      const choices = filterData?.dropdowns?.experiment_id?.choices || [];
      return (
        <td className="px-1 py-1 border-r border-gray-100">
          <select
            ref={inputRef as any}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
              if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            className="w-full px-2 py-1 text-xs text-gray-900 bg-white border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">—</option>
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </td>
      );
    }

    return (
      <td className="px-1 py-1 border-r border-gray-100">
        <input
          ref={inputRef}
          type={isNumericType(column.type) ? "number" : "text"}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          className="w-full px-2 py-1 text-xs text-gray-900 bg-white border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </td>
    );
  }

  const dirtyClasses = isDirty
    ? "bg-amber-100 ring-1 ring-inset ring-amber-400 text-amber-900 font-semibold"
    : "hover:bg-gray-50 text-gray-700";

  const isSmilesCell = column.name === "il_smiles";
  
  // Try to construct the SVG URL if index or lnp_id exists
  let svgUrl = "";
  if (isSmilesCell && row) {
    if (row.index !== undefined && row.index !== null) {
      svgUrl = `/media/ionizableLipid/LNP_${String(row.index).padStart(4, "0")}.svg`;
    } else if (row.lnp_id) {
      svgUrl = `/media/ionizableLipid/${row.lnp_id}.svg`;
    }
  }

  return (
    <td
      onDoubleClick={startEdit}
      className={`px-3 py-2 text-xs cursor-pointer whitespace-nowrap border-r border-gray-100 relative group ${dirtyClasses}`}
      title="Double-click to edit · Enter saves · Esc cancels"
    >
      <div className="flex items-center gap-2">
        <span className="truncate max-w-[200px]">{formatDisplay(value)}</span>
        {svgUrl && (
          <div className="relative flex items-center justify-center">
            <span className="text-gray-400 group-hover:text-blue-500">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </span>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 p-2 bg-white border border-gray-200 rounded-lg shadow-xl">
              <img src={svgUrl} alt="Structure Preview" className="w-48 h-48 object-contain bg-white" />
            </div>
          </div>
        )}
      </div>
    </td>
  );
};

export default EditableCell;
